
interface MetaData {
    title: string;
    description: string;
    robots?: string;
    canonical?: string;
    og?: {
        locale?: string,
        type?: string,
        title?: string,
        description?: string,
        url?: string,
        site_name?: string,
        updated_time?: string,
        image?: string,
        image_secure_url?: string,
        image_width?: number,
        image_height?: number,
        image_alt?: string,
        image_type?: string,
        video?: string,
        video_duration?: number,
        video_upload_date?: string,
        video_allow_embed?: boolean,
    };
    twitter?: {
        card?: string,
        title?: string,
        description?: string,
        image?: string,
    };
}

interface Step {
    title: string;
    desc: string;
    icon: string;
}

interface ProcedureSteps {
    heading: string;
    para: string;
    steps: Step[];
}

interface ListItem {
    title: string;
    desc: string;
    icon: string;
}

interface ListSection {
    heading?: string;
    subheading?: string;
    para?: string;
    items: ListItem[];
}

interface ingredients {
    heading?: string;
    subheading?: string;
    para?: string;
    items: ListItem[];
}

interface FAQItem {
    question: string;
    answer: string;
}

interface FAQSection {
    heading?: string;
    subheading?: string;
    items: FAQItem[];
}

interface HeroSection {
    title: string;
    subtitle: string;
    description: string;
}

interface HowItWorks {
    title: string;
    description: string;
}

interface TreatmentArea {
    ar: string;
    title: string;
    image: string;
}

interface LanguageContent {
    url?: string; // Added url property
    meta: MetaData;
    breadcrumbs: string[];
    hero: HeroSection;
    howItWorks: HowItWorks;
    procedureSteps: ProcedureSteps;
    benefits: ListSection;
    eligibility: ListSection;
    preparationTips: ListSection;
    results: ListSection;
    // aftercareTips: ListSection;
    faqData: FAQSection;
    ingredients: ingredients;
    // treatmentAreas1: TreatmentArea[];
    // treatmentAreas2: TreatmentArea[];
}

interface SharedData {
    images: {
        main: string,
        beforeAfter: string[] | any,
        introductionImage?: string,
    };
    product: {
        id: number,
        title: string,
        price: number,
        description: string,
    };
}

export const immuneboost: {
    en: LanguageContent,
    ar: LanguageContent,
    shared: SharedData,
} =
{
    "en": {
        "url": "immune-booster-iv-drip",
        "meta": {
            "title": "Immune Booster IV Drip - Elite Body Home",
            "description": "Strengthen your body's natural defense systems with our Immune Booster IV Drip, a potent therapy that infuses vitamins, minerals, and antioxidants directly into your bloodstream for enhanced immunity.",
            "robots": "follow, index",
            "canonical": "https://elitebodyhome.com/services/immune-booster-iv-drip/",
            "og": {
                "locale": "en_US",
                "type": "article",
                "title": "Immune Booster IV Drip - Elite Body Home",
                "description": "Potent IV therapy to strengthen your body's natural defense systems with vitamins, minerals and antioxidants.",
                "url": "https://elitebodyhome.com/services/immune-booster-iv-drip/",
                "site_name": "elitebodyhome.com"
            }
        },
        "breadcrumbs": ["Home", "Services", "Immune Booster IV Drip"],
        "hero": {
            "title": "Immune Booster IV Drip",
            "subtitle": "Strengthen Your Natural Defenses",
            "description": "Maintaining a healthy immune system is essential in this fast-paced life that we are living in today. The Immune Booster IV Drip is a potent therapy intended to strengthen your body's natural defense systems by infusing a mix of vitamins, minerals, and antioxidants directly into your bloodstream."
        },
        "howItWorks": {
            "title": "How the Immune Booster IV Drip Works",
            "description": "The treatment administers vital nutrients straight into your bloodstream, skipping the digestive tract. It helps the body utilize nutrients faster and more effectively than oral supplements."
        },
        "procedureSteps": {
            "heading": "Procedure Steps",
            "para": "The Immune Booster IV Drip process is simple and effective:",
            "steps": [
                {
                    "title": "Consultation",
                    "desc": "A healthcare professional checks your medical history and current health state to confirm that the IV drip is right for you.",
                    "icon": "fa-solid fa-comment-medical"
                },
                {
                    "title": "Preparation",
                    "desc": "The IV solution itself is a combination of vitamins, antioxidants, and immune-boosting nutrients.",
                    "icon": "fa-solid fa-flask"
                },
                {
                    "title": "IV Insertion",
                    "desc": "A trained healthcare professional gently inserts a small, sterile needle into a vein, usually in your arm.",
                    "icon": "fa-solid fa-syringe"
                },
                {
                    "title": "Infusion",
                    "desc": "The IV drip is delivered slowly over a period of 30-60 minutes and the nutrients enter your blood stream immediately.",
                    "icon": "fa-solid fa-clock"
                },
                {
                    "title": "Post-Treatment",
                    "desc": "Once the IV has dripped, the needle is removed, and you get to rest a few minutes. Patients generally feel invigorated and renewed soon after the session.",
                    "icon": "fa-solid fa-check"
                }
            ]
        },
        "benefits": {
            "heading": "Benefits",
            "subheading": "Key advantages of Immune Booster IV Therapy",
            "para": "The benefits of the Immune Booster IV Drip extend well beyond immune system support:",
            "items": [
                {
                    "title": "Rapid Recovery from Illness",
                    "desc": "Assist in Recovering Quickly from Cold, Flu, Or Other Viral Infections",
                    "icon": "fa-solid fa-virus-slash"
                },
                {
                    "title": "Boosts Energy Levels",
                    "desc": "Replenishes essential nutrients simultaneously turns fatigue into energy",
                    "icon": "fa-solid fa-bolt"
                },
                {
                    "title": "Improved Immune Function",
                    "desc": "Boost Your immunity power & boost anti-oxidant.",
                    "icon": "fa-solid fa-shield-virus"
                },
                {
                    "title": "Anti-Aging Benefits",
                    "desc": "The antioxidants fight free radicals, a process that slows down aging.",
                    "icon": "fa-solid fa-clock-rotate-left"
                },
                {
                    "title": "Hydration",
                    "desc": "Delivers instant hydration which is essential for overall health.",
                    "icon": "fa-solid fa-droplet"
                },
                {
                    "title": "Stress Reduction",
                    "desc": "Helps handle stress better by replenishing key vitamins and minerals.",
                    "icon": "fa-solid fa-brain"
                }
            ]
        },
        "ingredients": {
            "heading": "Ingredients",
            "subheading": "Powerful immune-boosting nutrients",
            "para": "Our formula contains scientifically-proven ingredients to support immune health:",
            "items": [
                {
                    "title": "Ascorbic acid (Vit C)",
                    "desc": "Essential vitamin for immune function",
                    "icon": "fa-solid fa-capsules"
                },
                {
                    "title": "L-glutamine",
                    "desc": "Amino acid that supports immune cell function",
                    "icon": "fa-solid fa-capsules"
                },
                {
                    "title": "B Vitamins Complex",
                    "desc": "Including B1, B6, B12 for energy and immune support",
                    "icon": "fa-solid fa-capsules"
                },
                {
                    "title": "Zinc Chloride",
                    "desc": "Critical mineral for immune cell development",
                    "icon": "fa-solid fa-capsules"
                }
            ]
        },
        "eligibility": {
            "heading": "Eligibility",
            "subheading": "Ideal candidates for Immune Booster IV Therapy",
            "para": "An Immune Booster IV Drip may not be needed by everyone, but some individuals can get significant benefits:",
            "items": [
                {
                    "title": "Frequent Travelers",
                    "desc": "Exposure to different environments and to different germs can help to strengthen the immune response.",
                    "icon": "fa-solid fa-plane"
                },
                {
                    "title": "Those Recovering from Illness",
                    "desc": "Great for people who've recently had a cold, flu or infection.",
                    "icon": "fa-solid fa-head-side-cough"
                },
                {
                    "title": "Individuals with chronic fatigue syndrome",
                    "desc": "If you're always feeling tired, the drip can help you regain energy and balance.",
                    "icon": "fa-solid fa-bed"
                },
                {
                    "title": "Very Active Persons or Athletes",
                    "desc": "Physical stressors can compromise the immune system, and this therapy assists recovery and bolsters immunity.",
                    "icon": "fa-solid fa-person-running"
                }
            ]
        },
        "preparationTips": {
            "heading": "Preparation",
            "subheading": "How to prepare for your IV Drip session",
            "para": "The prep for your Immune Booster IV Drip is simple:",
            "items": [
                {
                    "title": "Hydrate",
                    "desc": "Though the drip hydrates, it's still beneficial to have a watery beverage prior to your session.",
                    "icon": "fa-solid fa-glass-water"
                },
                {
                    "title": "Eat Light",
                    "desc": "Eat a small meal before as a precaution to avoid lightheadedness.",
                    "icon": "fa-solid fa-utensils"
                },
                {
                    "title": "Wear Comfortable Clothing",
                    "desc": "You're going to want to be relaxed and comfortable during the procedure.",
                    "icon": "fa-solid fa-shirt"
                },
                {
                    "title": "Notify Your Provider",
                    "desc": "Inform your health care provider about all medications or supplements you're taking.",
                    "icon": "fa-solid fa-file-medical"
                }
            ]
        },
        "results": {
            "heading": "Expected Results",
            "subheading": "What to expect after treatment",
            "para": "Immune Booster IV Drip has rapid and excellent results:",
            "items": [
                {
                    "title": "Increased Energy",
                    "desc": "Start feeling more energized and alert within a day or two of treatment.",
                    "icon": "fa-solid fa-battery-full"
                },
                {
                    "title": "Enhanced Immunity",
                    "desc": "Better resistance to infections, especially in flu season or stressful times.",
                    "icon": "fa-solid fa-shield-virus"
                },
                {
                    "title": "Quick Recovery",
                    "desc": "Quicker recovery times from illness or fatigue.",
                    "icon": "fa-solid fa-clock-rotate-left"
                }
            ]
        },
        "faqData": {
            "heading": "Frequently Asked Questions",
            "subheading": "Common questions about Immune Booster IV Therapy",
            "items": [
                {
                    "question": "How often should I get an Immune Booster IV Drip?",
                    "answer": "Frequency depends on your individual needs - some benefit from monthly maintenance, others during flu season or periods of high stress."
                },
                {
                    "question": "Are there any side effects?",
                    "answer": "Side effects are rare but may include minor bruising at the injection site. Most patients report only positive effects."
                },
                {
                    "question": "How quickly will I feel the effects?",
                    "answer": "Many patients report feeling more energized within hours, with full immune benefits developing over several days."
                },
                {
                    "question": "Can I get this if I'm sick?",
                    "answer": "Yes, it can help speed recovery when you're already ill, though best results come from preventive use."
                }
            ]
        }
    },
    "ar": {
        "url": "تعزيز-المناعة-بالتنقيط-الوريدي",
        "meta": {
            "title": "علاج تعزيز المناعة بالتنقيط الوريدي | إيليت بودي هوم",
            "description": "عزز جهازك المناعي بعلاج تعزيز المناعة بالتنقيط الوريدي الذي يوفر مزيجًا من الفيتامينات والمعادن ومضادات الأكسدة مباشرة في مجرى الدم.",
            "robots": "index, follow",
            "canonical": "https://elitebodyhome.com/services/تعزيز-المناعة-بالتنقيط-الوريدي/",
            "og": {
                "title": "علاج تعزيز المناعة بالتنقيط الوريدي | إيليت بودي هوم",
                "description": "علاج فعال لتعزيز أنظمة الدفاع الطبيعية بخلطة من الفيتامينات والمعادن ومضادات الأكسدة.",
                "url": "https://elitebodyhome.com/services/تعزيز-المناعة-بالتنقيط-الوريدي/"
            }
        },
        "breadcrumbs": ["الرئيسية", "الخدمات", "تعزيز المناعة بالتنقيط الوريدي"],
        "hero": {
            "title": "علاج تعزيز المناعة بالتنقيط الوريدي",
            "subtitle": "عزّز مناعتك، وأنعش جسمك",
            "description": "أصبح الحفاظ على جهاز مناعي صحي أمرًا بالغ الأهمية في ظل نمط الحياة السريع الذي نعيشه اليوم. يُعد علاج تعزيز صحة جهاز المناعة بالتنقيط الوريدي علاج فعال مصمم خصيصًا لتعزيز أنظمة الدفاع الطبيعية لجسمك من خلال ضخ مزيج من الفيتامينات والمعادن والمواد المغذية ومضادات الأكسدة مباشرة في مجرى الدم."
        },
        "howItWorks": {
            "title": "كيف يعمل العلاج؟",
            "description": "يوفر هذا العلاج العناصر الغذائية الأساسية مباشرة إلى مجرى الدم، متجاوزًا الجهاز الهضمي، مما يسمح للجسم بامتصاص المغذيات بشكل أسرع وأكثر فاعلية مقارنةً بالمكملات الغذائية التي تؤخذ عن طريق الفم."
        },
        "procedureSteps": {
            "heading": "خطوات الإجراء",
            "para": "إجراء بسيط وفعال لتعزيز المناعة:",
            "steps": [
                {
                    "title": "استشارة الطبيب",
                    "desc": "يبدأ العلاج باستشارة مع الطبيب لمراجعة تاريخك الطبي وتقييم حالتك الصحية الحالية والتأكد من ملاءمة العلاج لك.",
                    "icon": "fa-solid fa-comment-medical"
                },
                {
                    "title": "الاستعداد",
                    "desc": "يتكون محلول التنقيط من مزيج من الفيتامينات، ومضادات الأكسدة، والعناصر الغذائية المعززة للمناعة.",
                    "icon": "fa-solid fa-flask"
                },
                {
                    "title": "إدخال الإبرة الوريدية",
                    "desc": "يقوم أخصائي الرعاية الصحية المدرب بإدخال إبرة صغيرة ومعقمة في أحد أوردة الذراع بلطف.",
                    "icon": "fa-solid fa-syringe"
                },
                {
                    "title": "التنقيط الوريدي",
                    "desc": "يُعطى المحلول عبر الوريد ببطء على مدار 30 إلى 60 دقيقة، حيث تبدأ المغذيات بالدخول إلى الدورة الدموية على الفور بدعم وظيفة المناعة.",
                    "icon": "fa-solid fa-clock"
                },
                {
                    "title": "الرعاية اللاحقة",
                    "desc": "تُزال الإبرة بعد انتهاء التنقيط الوريدي وتُمنح بضع دقائق للراحة. غالبًا ما يشعر المرضى بالنشاط والانتعاش بعد الجلسة مباشرةً.",
                    "icon": "fa-solid fa-check"
                }
            ]
        },
        "benefits": {
            "heading": "الفوائد",
            "subheading": "أبرز فوائد العلاج",
            "para": "تتجاوز فوائد هذا العلاج مجرد تعزيز جهاز المناعة:",
            "items": [
                {
                    "title": "التعافي السريع من الأمراض",
                    "desc": "يساعد في تسريع التعافي من نزلات البرد، أو الإنفلونزا أو أي عدوى فيروسية أخرى.",
                    "icon": "fa-solid fa-virus-slash"
                },
                {
                    "title": "تعزيز مستويات الطاقة",
                    "desc": "يعيد توازن العناصر الغذائية الأساسية في الجسم، مما يحول الإرهاق إلى نشاط.",
                    "icon": "fa-solid fa-bolt"
                },
                {
                    "title": "دعم قوي لوظائف المناعة",
                    "desc": "يعزز كفاءة الجهاز المناعي ويزيد من فاعلية مضادات الأكسدة في الجسم.",
                    "icon": "fa-solid fa-shield-virus"
                },
                {
                    "title": "فوائد مضادة للشيخوخة",
                    "desc": "تحتوي التركيبة على مضادات أكسدة مثل الجلوتاثيون، التي تحارب الجذور الحرة وتساهم في إبطاء علامات التقدم في العمر.",
                    "icon": "fa-solid fa-clock-rotate-left"
                },
                {
                    "title": "ترطيب فوري",
                    "desc": "يوفر الترطيب اللازم فورًا للجسم، وهو أساسي للصحة العامة.",
                    "icon": "fa-solid fa-droplet"
                },
                {
                    "title": "تقليل التوتر",
                    "desc": "يساعد على استعادة توازن الفيتامينات والمعادن، مما يعزز قدرة الجسم على التعامل مع الإجهاد.",
                    "icon": "fa-solid fa-brain"
                }
            ]
        },
        "ingredients": {
            "heading": "المكونات الأساسية",
            "subheading": "عناصر غذائية معززة للمناعة",
            "para": "تحتوي التركيبة على مكونات مدعومة علميًا لدعم المناعة:",
            "items": [
                {
                    "title": "حمض الأسكوربيك (فيتامين سي)",
                    "desc": "فيتامين أساسي لوظيفة المناعة",
                    "icon": "fa-solid fa-capsules"
                },
                {
                    "title": "إل جلوتامين",
                    "desc": "حمض أميني يدعم وظيفة الخلايا المناعية",
                    "icon": "fa-solid fa-capsules"
                },
                {
                    "title": "مجموعة فيتامينات ب",
                    "desc": "تشمل B1، B6، B12 للطاقة ودعم المناعة",
                    "icon": "fa-solid fa-capsules"
                },
                {
                    "title": "كلوريد الزنك",
                    "desc": "معدن حاسم لنمو الخلايا المناعية",
                    "icon": "fa-solid fa-capsules"
                }
            ]
        },
        "eligibility": {
            "heading": "الأهلية",
            "subheading": "المرشحون المناسبون للعلاج",
            "para": "قد لا يحتاج الجميع إلى هذا العلاج، لكن يمكن لبعض الأشخاص الاستفادة منه بشكل كبير:",
            "items": [
                {
                    "title": "المسافرون الدائمون",
                    "desc": "يسبب التنقل المستمر والتعرض لبيئات مختلفة تعرض الجسم لمجموعة متنوعة من الجراثيم، مما يستدعي دعم الجهاز المناعي بشكل منتظم.",
                    "icon": "fa-solid fa-plane"
                },
                {
                    "title": "المتعافون من الأمراض",
                    "desc": "يعد علاج مناسب إذا كنت قد تعافيت مؤخرًا من نزلة برد أو إنفلونزا أو عدوى فيروسية.",
                    "icon": "fa-solid fa-head-side-cough"
                },
                {
                    "title": "المصابون بمتلازمة الإرهاق المزمن",
                    "desc": "إذا كنت تعاني من إرهاق دائم، فقد يساعد التنقيط على استعادة التوازن والطاقة.",
                    "icon": "fa-solid fa-bed"
                },
                {
                    "title": "الأشخاص النشطون جدًا أو الرياضيون",
                    "desc": "قد يضعف الإرهاق الجسدي جهاز المناعة، لذا يُنصح بتعزيز المناعة ودعم التعافي من خلال التنقيط الوريدي.",
                    "icon": "fa-solid fa-person-running"
                }
            ]
        },
        "preparationTips": {
            "heading": "الاستعداد للجلسة",
            "subheading": "كيف تستعد لعلاجك",
            "para": "التحضير للجلسة سهل وبسيط:",
            "items": [
                {
                    "title": "الحفاظ على ترطيب",
                    "desc": "على الرغم من أن الجلسة ستوفر الترطيب لجسمك، إلا أنه من المفيد شرب كمية كافية من الماء أو مشروب مرطب قبل الموعد.",
                    "icon": "fa-solid fa-glass-water"
                },
                {
                    "title": "تناول وجبة خفيفة",
                    "desc": "يُنصح بتناول وجبة خفيفة قبل الجلسة لتفادي الشعور بالدوار أو انخفاض مستوى السكر في الدم أثناء العلاج.",
                    "icon": "fa-solid fa-utensils"
                },
                {
                    "title": "ارتداء ملابس مريحة",
                    "desc": "اختر ملابس فضفاضة ومريحة تساعدك على الاسترخاء أثناء الجلسة.",
                    "icon": "fa-solid fa-shirt"
                },
                {
                    "title": "إبلاغ الطبيب بالأدوية",
                    "desc": "أخبر مقدم الرعاية الصحية بأي أدوية أو مكملات غذائية تتناولها لضمان أمان الجلسة.",
                    "icon": "fa-solid fa-file-medical"
                }
            ]
        },
        "results": {
            "heading": "النتائج المتوقعة",
            "subheading": "ما يمكن توقعه بعد العلاج",
            "para": "تُظهر الجلسات نتائج سريعة وفعالة:",
            "items": [
                {
                    "title": "زيادة الطاقة",
                    "desc": "يشعر معظم المرضى بارتفاع في مستويات النشاط والتركيز خلال يوم أو يومين فقط بعد الجلسة.",
                    "icon": "fa-solid fa-battery-full"
                },
                {
                    "title": "تعزيز المناعة",
                    "desc": "تحسين قدرة الجسم على مقاومة العدوى، خصوصًا خلال مواسم الأنفلونزا أو فترات الإجهاد الشديد.",
                    "icon": "fa-solid fa-shield-virus"
                },
                {
                    "title": "التعافي السريع",
                    "desc": "تسريع التعافي من الإرهاق أو الأمراض.",
                    "icon": "fa-solid fa-clock-rotate-left"
                }
            ]
        },
        "faqData": {
            "heading": "الأسئلة الشائعة",
            "subheading": "أسئلة متداولة حول العلاج",
            "items": [
                {
                    "question": "كم مرة يجب أن أحصل على جلسة تعزيز المناعة؟",
                    "answer": "يعتمد التكرار على الاحتياجات الفردية - البعض يستفيد من الجلسات الشهرية، والبعض الآخر خلال مواسم البرد أو فترات الضغط العالي."
                },
                {
                    "question": "هل هناك أي آثار جانبية؟",
                    "answer": "الآثار الجانبية نادرة وقد تشمل كدمات طفيفة في موقع الحقن. معظم المرضى يبلغون عن تأثيرات إيجابية فقط."
                },
                {
                    "question": "ما هي سرعة الشعور بالتحسن؟",
                    "answer": "يبلغ العديد من المرضى عن زيادة الطاقة خلال ساعات، مع اكتمال فوائد المناعة خلال عدة أيام."
                },
                {
                    "question": "هل يمكن الحصول على العلاج أثناء المرض؟",
                    "answer": "نعم، يمكن أن يساعد في تسريع الشفاء عند الإصابة بالمرض، رغم أن أفضل النتائج تكون عند الاستخدام الوقائي."
                }
            ]
        }
    },
    "shared": {
        "images": {
            "main": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/New-Service-Baetergnner-Image-scaled.jpg",
            "beforeAfter": []
        },
        "product": {
            "id": 35,
            "title": "Immune Booster IV Drip Treatment",
            "description": "Specialized intravenous therapy delivering immune-supporting nutrients and antioxidants directly to your bloodstream for enhanced immunity and faster recovery.",
            "price": 550
        }
    }
}